import './styles.css';

// import CharacterCount from "@tiptap/extension-character-count";

import { Button } from '@/components/ui/button';
import { CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { commentStore, dealStore } from '@/models/stores';
import {
  CommentTargetType,
  type Comment,
  type Deal,
  type User,
} from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { isEmpty, trim } from 'lodash';
import { PaperclipIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo } from 'react';
import { getUserMentionIds } from './get-user-mentions-ids';
import PaddockBubbleMenu from './paddock-bubble-menu';
import PdkEditorContent from './pdk-editor-content';
import { usePaddockEditor } from './use-paddock-editor';

export const OPTIMISTIC_COMMENT_ID_PREFIX = 'optimistic';

// TODO (jimmy) TECH DEBT: this is a copy of AddCommentTiptap
// we should consolidate this

const EditCommentTiptap = ({
  dealId,
  comment,
  setIsEditingComment,
}: {
  dealId: Deal['id'];
  comment: Deal['comments'][number] | Comment;
  setIsEditingComment: (isEditing: boolean) => void;
}) => {
  // TODO (jimmy) TECH DEBT: Consolidate this with MessageTipTap
  // into a useParseContent hook
  const initialContent = useMemo(() => {
    try {
      // DEV NOTE: comments and replies are stringified json
      // thus we need to parse it
      return comment.content;
    } catch (error) {
      console.error('Error parsing initialContent', error);
      return '';
    }
  }, [comment.content]);

  const { editor } = usePaddockEditor({
    initialContent,
  });

  const user = useUser();

  const resetEditor = useCallback(() => {
    editor?.commands.setContent('');
  }, [editor]);

  const editCommentV3 = useMutation({
    onMutate: async ({ comment }: { comment: Comment }) => {
      const dealStoreComment = {
        id: comment.id,
        commentable_id: comment.commentable_id,
        commentable_type: comment.commentable_type,
        created_at: comment.created_at,
        updated_at: comment.updated_at,
        content: comment.content,
        text: comment.text,
        edited: true,
        replies: [],
        user_id: user.id,
      };
      dealStore.replaceCommentAndDedupeV3({
        idToReplace: comment.id,
        comment: dealStoreComment,
        dealId,
      });
    },
    mutationFn: async ({
      comment,
    }: {
      comment: Comment & {
        text: string;
        mentions: User['id'][];
      };
    }) => {
      if (!comment) return;

      const response = await pdkAxios
        .post(`/deals/${dealId}/comments/v3/${comment.id}/edit`, {
          commentable_id: comment.commentable_id,
          commentable_type: comment.commentable_type,
          content: comment.content,
          text: comment.text,
          mentions: comment.mentions,
        })
        .catch((e) => {
          console.error('Error editing comment', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      setIsEditingComment(false);
    },
    onError: (error, variables) => {
      // UX Note: restore the content of the editor
      editor?.commands.setContent(variables?.comment?.content);
      dealStore.removeCommentV3({
        id: dealId,
        commentId: variables?.comment?.id,
      });
      toast({
        title: 'Error editing comment',
        description: error.message,
      });
      console.error('Error editing polymorphic comment', error, variables);
    },
  });

  const handleSubmitComment = () => {
    // TECH DEBT: handle trimming whitespace from `content`
    // this requires writing a function that remove hardBreaks
    // and texts that are empty
    // and trimming whitespace from the text
    const content = editor?.getJSON();

    const text = editor?.getText() || '';

    if (isEmpty(trim(text))) {
      return;
    }

    const mentionIds = getUserMentionIds(editor);

    const payload = {
      comment: {
        id: comment.id,
        created_at: comment.created_at,
        updated_at: dayjs().toISOString(),
        content: content || {},
        text,
        mentions: mentionIds,
        deal_id: dealId,
        commentable_id: dealId,
        commentable_type: CommentTargetType.Deal,
        edited: true,
        replies: comment.replies,
        user_id: user.id,
      },
    };

    resetEditor();

    if (!!commentStore.getById(comment.id)) {
      editCommentV3.mutate(payload);
    }
  };

  const isExpanded = true;

  useEffect(() => {
    editor?.commands.focus('end');
  }, [editor]);

  const handleClose = useCallback(() => {
    setIsEditingComment(false);
  }, [setIsEditingComment]);

  return (
    <div>
      <div className="flex flex-col gap-4">
        <CardHeader className="p-0">
          <CardDescription>
            <PaddockBubbleMenu editor={editor} />

            <PdkEditorContent
              editor={editor}
              onKeyDown={(e) => {
                if (
                  (e.key === 'Enter' && e.metaKey) ||
                  (e.key === 'Enter' && e.ctrlKey)
                ) {
                  e.preventDefault();
                  handleSubmitComment();
                }

                if (e.key === HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE) {
                  handleClose();
                }
              }}
            />
          </CardDescription>
        </CardHeader>
        <CardFooter className="p-0">
          <motion.div
            className="flex flex-row w-full justify-between gap-2 h-10 items-center py-2"
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: isExpanded ? 'auto' : 0,
              opacity: isExpanded ? 1 : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            <PaperclipIcon
              className={cn(
                'size-4 cursor-pointer text-gray-500 hover:fill-gray-100',
                {},
              )}
              onClick={() => {
                toast({
                  title: 'Feature not implemented yet',
                  description:
                    "Slack us if you want the 'comments attachments' feature",
                });
              }}
            />

            <div className="flex flex-row gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={handleClose}>
                    Cancel
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row gap-1 items-center">
                  <p>Cancel</p>
                  <KeyboardKeyIcon>
                    {HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE}
                  </KeyboardKeyIcon>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSubmitComment}
                  >
                    Save
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row gap-1 items-center">
                  <p>Submit Comment</p>
                  <KeyboardKeyIcon>
                    {HOTKEYS.DEAL_SUBMIT_COMMENT}
                  </KeyboardKeyIcon>
                </TooltipContent>
              </Tooltip>
            </div>
          </motion.div>
        </CardFooter>
      </div>
    </div>
  );
};

export default EditCommentTiptap;
